/**
 * @fileoverview Markdown parsing and HTML sanitization utilities.
 *
 * Provides safe markdown-to-HTML conversion with XSS protection via DOMPurify.
 * All links are configured to open in new tabs for security.
 *
 * @module helpers/markdown
 */

const { marked } = require("marked");
import dompurify from "dompurify";

/** @type {marked.Renderer} Custom marked renderer for link handling */
const renderer = new marked.Renderer();

/**
 * DOMPurify hook that forces all links to open in new tabs.
 * Applied after attribute sanitization for security.
 */
dompurify.addHook("afterSanitizeAttributes", function (node) {
  if (node.hasAttribute("xlink:href") || node.hasAttribute("href")) {
    node.setAttribute("target", "_blank");
  }
});

/**
 * Checks if a string contains HTML tags.
 *
 * @param {string} text - The text to check
 * @returns {boolean} True if text contains HTML tags
 *
 * @example
 * isHTML('<p>Hello</p>');  // true
 * isHTML('Hello World');   // false
 * isHTML('<br/>');         // true
 */
export const isHTML = (text: string): boolean => {
  const isHtmlRegex = /<\/?[a-z][\s\S]*>|(<\/[a-z][\s\S]*>|.+\/>)/i;
  return isHtmlRegex.test(text);
};

/**
 * Converts markdown content to sanitized HTML.
 *
 * Features:
 * - GitHub Flavored Markdown (GFM) support
 * - Line break conversion (breaks: true)
 * - Custom link styling with configurable color
 * - XSS protection via DOMPurify sanitization
 * - All links open in new tabs
 *
 * @param {string} content - Markdown content to convert
 * @param {string} [agentTextColor='black'] - CSS color for link text
 * @returns {string} Sanitized HTML string
 *
 * @example
 * mdToHtml('**Bold** and [link](https://example.com)');
 * // Returns: '<strong>Bold</strong> and <a href="..." target="_blank">link</a>'
 *
 * mdToHtml('Click [here](url)', 'blue');
 * // Returns link with blue text color
 */
export const mdToHtml = (content: string, agentTextColor: string = "black") => {
  renderer.link = function (href, title, text) {
    return `<a href='${href}' title='${title}' style='color: ${agentTextColor};' target='_blank' on-click="event.stopPropagation();">${text}</a>`;
  };
  const html = marked(content, {
    renderer,
    gfm: true,
    breaks: true,
  });
  return dompurify.sanitize(html);
};
